import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, Award, Building2, CheckCircle, ChevronRight, Shield, Target, Zap } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Hero3D from "../components/Hero3D";
import useCounter from "../hooks/useCounter";

import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const HERO_VIDEO = "/assets/heropage.png"; // Modern generic corporate video
const COMMAND_CENTER_IMG = "/assets/howwework.png";

const FadeUp = ({ children, delay = 0, className = "" }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-100px" }}
      transition={{ duration: 0.8, delay: delay / 1000, ease: [0.16, 1, 0.3, 1] }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

const StatItem = ({ value, suffix, label, displayText, startCounting }) => {
  const count = useCounter(value || 0, 1800, startCounting);
  return (
    <div className="text-center px-4 py-8 lg:py-12 border-[#141414]/10 relative group">
      <div className="absolute inset-0 bg-[#835F21]/0 group-hover:bg-[#835F21]/5 transition-colors duration-500 rounded-2xl mx-2" />
      <div className="relative z-10">
        <div className="font-display font-medium text-4xl md:text-5xl text-[#141414] mb-3 tracking-tight">
          {displayText ? displayText : `${count.toLocaleString()}${suffix}`}
        </div>
        <div className="font-sans text-[10px] md:text-[11px] font-bold tracking-[0.25em] uppercase text-[#141414]/60">{label}</div>
      </div>
    </div>
  );
};

const services = [
  {
    icon: Shield,
    title: "Security Solutions",
    desc: "Deploying certified, professionally trained personnel for comprehensive asset protection and specialized security solutions.",
    to: "/services",
  },
  {
    icon: Building2,
    title: "Facilities Management",
    desc: "Ensuring seamless facility operations. Providing continuous upkeep, advanced technical maintenance, and hygiene services.",
    to: "/services",
  },
  {
    icon: Zap,
    title: "Consultancy & Risk",
    desc: "Strategic advisory services focused on identifying vulnerabilities and engineering resilient operational frameworks.",
    to: "/services",
  },
];

const trustPoints = [
  { 
    icon: Award, 
    label: "PSARA Licensed", 
    desc: "Fully authorized by state authorities to provide professional security services." 
  },
  { 
    icon: CheckCircle, 
    label: "Statutory Compliant", 
    desc: "100% adherence to ESI, PF, and labor norms for workforce stability." 
  },
  { 
    icon: Shield, 
    label: "Personnel Verified", 
    desc: "Thorough police background checks and identity verification for every operative." 
  },
  { 
    icon: Target, 
    label: "24/7 Monitoring", 
    desc: "Central supervision for incident response and reporting." 
  },
];

const ComplianceCard = ({ point, delay }) => {
  const cardRef = useRef(null);
  const bgRef = useRef(null);
  const titleRef = useRef(null);
  const descRef = useRef(null);
  const iconRef = useRef(null);
  const iconBoxRef = useRef(null);
  const tweenRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      tweenRef.current = gsap.timeline({ paused: true })
        .to(bgRef.current, { 
          clipPath: "circle(150% at 0% 0%)", 
          duration: 0.8, 
          ease: "power2.inOut" 
        }, 0)
        .to(titleRef.current, { color: "#ffffff", duration: 0.6, ease: "power2.out" }, 0.1)
        .to(descRef.current, { color: "rgba(255, 255, 255, 0.8)", duration: 0.6, ease: "power2.out" }, 0.1)
        .to(iconBoxRef.current, { backgroundColor: "rgba(255, 255, 255, 0.2)", duration: 0.6, ease: "power2.out" }, 0)
        .to(iconRef.current, { color: "#ffffff", duration: 0.6, ease: "power2.out" }, 0);
    }, cardRef);

    return () => ctx.revert();
  }, []);

  const handleMouseEnter = () => tweenRef.current?.play();
  const handleMouseLeave = () => tweenRef.current?.reverse();

  return (
    <FadeUp delay={delay} className="h-full">
      <div 
        ref={cardRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        className="group relative bg-white rounded-3xl p-8 border border-[#141414]/5 transition-all duration-500 overflow-hidden h-full flex flex-col cursor-pointer shadow-[0_20px_40px_rgba(0,0,0,0.02)]"
      >
        <div 
          ref={bgRef} 
          className="absolute inset-0 bg-[#835F21] z-0 pointer-events-none" 
          style={{ clipPath: "circle(0% at 0% 0%)" }}
        />
        <div className="relative z-10 flex flex-col h-full pointer-events-none">
          <div ref={iconBoxRef} className="w-12 h-12 rounded-xl bg-[#835F21]/10 flex items-center justify-center mb-6 transition-colors duration-500">
            <point.icon ref={iconRef} size={22} className="text-[#835F21] transition-colors duration-500" strokeWidth={1.5} />
          </div>
          <h4 ref={titleRef} className="font-display font-medium text-lg text-[#141414] mb-3 tracking-tight transition-colors duration-500">{point.label}</h4>
          <p ref={descRef} className="font-sans text-[13px] text-[#141414]/50 leading-relaxed font-light transition-colors duration-500">{point.desc}</p>
        </div>
      </div>
    </FadeUp>
  );
};

const servicesList = [
  { name: "Security Services", img: "/assets/indian_security_home.png", focus: "Operational Guarding" },
  { name: "Security Solutions", img: "/assets/securityservices.png", focus: "Integrated Strategy" },
  { name: "Event Security", img: "/assets/event.png", focus: "Crowd & Venue Protec" },
  { name: "Executive Protection", img: "/assets/executive.png", focus: "Direct VIP Escort" },
  { name: "Industrial Security", img: "/assets/industry.png", focus: "Loss Prevention" },
  { name: "Risk Management", img: "/assets/risk.png", focus: "Consultancy & Audit" },
  { name: "Sports Events Security", img: "/assets/sports.png", focus: "Arena Safety" },
  { name: "Facilities Management", img: "/assets/image.png", focus: "Infrastructure Care" },
  { name: "Executive / CEO Protection", img: "https://images.unsplash.com/photo-1507679799987-c73779587ccf?w=600&q=80", focus: "Elite Leadership Safety" },
];

export default function Home() {
  const { scrollYProgress } = useScroll();
  const heroY = useTransform(scrollYProgress, [0, 0.2], [0, 100]);
  const heroScale = useTransform(scrollYProgress, [0, 0.2], [1, 1.05]);

  const sectorsWrapperRef = useRef(null);
  const sectorsRef = useRef(null);



  useEffect(() => {
    if (!sectorsRef.current || !sectorsWrapperRef.current) return;

    function getScrollAmount() {
      let racesWidth = sectorsRef.current.scrollWidth;
      // Precision scroll range to ensure last card snaps into view
      return -(racesWidth - window.innerWidth + window.innerWidth * 0.05);
    }

    const tween = gsap.to(sectorsRef.current, {
      x: getScrollAmount,
      duration: 3,
      ease: "none",
    });

    const scrollTrigger = ScrollTrigger.create({
      trigger: sectorsWrapperRef.current,
      start: "top 20%",
      end: () => `+=${getScrollAmount() * -1}`,
      pin: true,
      animation: tween,
      scrub: 0,
      invalidateOnRefresh: true,
    });

    return () => {
      scrollTrigger.kill();
      tween.kill();
    };
  }, []);

  return (
    <main className="bg-[#F9F8F5] overflow-hidden font-sans text-[#141414]" data-testid="home-page">
      {/* ── 01. Premium Hero Section ── */}
      <section
        className="relative min-h-[85vh] md:min-h-[75vh] lg:min-h-[80vh] flex items-center overflow-hidden bg-white"
        data-testid="home-hero"
      >
        {/* Background image with softer content fade for readability */}
        <div className="absolute inset-0 z-0">
          <div className="absolute inset-0 z-0 overflow-hidden">
            <img
              src={HERO_VIDEO}
              alt="Elite Security Personnel"
              className="w-full h-full object-cover object-[82%_center] opacity-95 scale-[1.02]"
            />
          </div>

          <div
            className="absolute inset-y-0 left-0 w-full lg:w-[40%] bg-[#F9F8F5]/90 backdrop-blur-sm z-10 pointer-events-none"
            style={{ clipPath: "polygon(0 0, 85% 0, 100% 100%, 0 100%)" }}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#141414]/10 via-transparent to-transparent z-10 pointer-events-none" />
          <div className="absolute inset-0 bg-[#835F21]/[0.03] mix-blend-multiply z-10 pointer-events-none" />
        </div>

        <div className="relative z-[15] hidden md:block">
          <Hero3D />
        </div>

        <div className="relative z-20 w-full lg:w-[45%] px-6 md:px-12 lg:pl-24 lg:pr-12 py-20 md:py-28 lg:py-32">
          <div className="relative z-10">
          
            <motion.h1 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-medium text-[#141414] leading-[1.05] mb-8 tracking-tight"
              style={{ fontSize: "clamp(20px, 3vw, 44px)" }}
            >
              Integrated Security. <br />
              <span className="text-[#835F21] italic font-light">Reliable Protection.</span>
            </motion.h1>

            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="font-sans text-lg md:text-xl text-[#141414]/70 leading-relaxed max-w-2xl mb-10 font-light"
            >
              Astraum Task Force delivers trained <br/>manpower and 
            structured security<br/> services to protect your people, assets,<br/> and operations.
            </motion.p>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
              className="flex flex-wrap gap-5"
            >
              <Link to="/contact" className="btn-primary min-w-[240px] h-14 text-sm group shadow-[0_20px_40px_rgba(0,0,0,0.05)] hover:shadow-[0_30px_60px_rgba(0,0,0,0.1)]">
                Request Deployment
                <ArrowRight size={18} className="ml-3 group-hover:translate-x-2 transition-transform duration-300" />
              </Link>
              <Link
                  to="/services"
                  className="btn-secondary border-[#141414]/20 text-[#141414] hover:bg-[#141414]/5 h-14 text-sm group"
              >
                Explore Capabilities
              </Link>
            </motion.div>
          </div>
        </div>
      </section>



      {/* ── 03. Core Capabilities (Enterprise Grade) ── */}
      <section className="bg-[#F9F8F5] py-16 md:py-24 relative overflow-hidden" data-testid="home-services">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="flex flex-col lg:flex-row gap-20 items-end mb-24">
            <FadeUp className="flex-1">
              <span className="section-label block mb-6 text-[#835F21]">Core Services</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.1] tracking-tight"
                style={{ fontSize: "clamp(44px, 5vw, 64px)" }}
              >
                Comprehensive <br />
                <span className="text-[#835F21] italic font-light">Security Services.</span>
              </h2>
            </FadeUp>
            <FadeUp delay={200} className="lg:w-1/3">
              <p className="font-sans text-lg text-[#141414]/70 leading-relaxed">
                We provide reliable, trained, and compliant manpower solutions across security and facility operations ensuring consistency, accountability, and smooth day-to-day functioning.
              </p>
            </FadeUp>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {services.map((s, i) => (
              <FadeUp key={i} delay={i * 150}>
                <Link to={s.to} className="group relative bg-white rounded-3xl p-10 lg:p-12 overflow-hidden transition-all duration-700 hover:shadow-[0_40px_80px_rgba(0,0,0,0.08)] border border-[#141414]/5 flex flex-col h-full hover:-translate-y-2">
                  <div className="w-16 h-16 rounded-2xl bg-[#835F21]/10 flex items-center justify-center mb-10 group-hover:bg-[#835F21] group-hover:scale-110 transition-all duration-500">
                    <s.icon size={28} className="text-[#835F21] group-hover:text-white transition-colors duration-500" strokeWidth={1.5} />
                  </div>
                  <h3 className="font-display font-medium text-2xl lg:text-3xl text-[#141414] mb-6 leading-tight tracking-tight">{s.title}</h3>
                  <p className="font-sans text-base text-[#141414]/60 leading-relaxed mb-10 flex-grow font-light">{s.desc}</p>
                  <div className="flex items-center justify-between border-t border-[#141414]/10 pt-6 mt-auto">
                     <span className="font-sans text-[11px] font-bold tracking-[0.15em] text-[#835F21] uppercase">
                       View Protocol
                     </span>
                     <div className="w-8 h-8 rounded-full border border-[#835F21]/30 flex items-center justify-center group-hover:bg-[#835F21] group-hover:text-white transition-all duration-300">
                       <ArrowRight size={14} />
                     </div>
                  </div>
                </Link>
              </FadeUp>
            ))}
          </div>

          {/* Trust Attributes Section */}
          <div className="mt-16 border-t border-[#141414]/10 pt-12">
            <FadeUp className="mb-10">
              <span className="section-label block mb-4 text-[#835F21]">Operational Integrity</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.2] tracking-tight"
                style={{ fontSize: "clamp(28px, 4vw, 42px)" }}
              >
                Compliance & <span className="text-[#835F21] italic font-light">Governance.</span>
              </h2>
            </FadeUp>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {trustPoints.map((point, i) => (
                <ComplianceCard key={i} point={point} delay={i * 100} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 04. The Command Center (Methodology) ── */}
      <section className="bg-[#F9F8F5] py-16 md:py-24 relative overflow-hidden" data-testid="home-methodology">
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 lg:gap-32 items-center">
            <FadeUp className="relative order-2 lg:order-1">
              <div className="relative rounded-[2rem] overflow-hidden shadow-[0_0_100px_rgba(0,0,0,0.08)] border border-[#141414]/10 group">
                <img 
                  src={COMMAND_CENTER_IMG} 
                  alt="ATF Command Control Center" 
                  className="w-full aspect-[4/5] object-cover transition-transform duration-1000 group-hover:scale-105"
                />

                <div className="absolute bottom-10 left-10 right-10">
                  <div className="backdrop-blur-md bg-[#141414]/5 border border-[#141414]/10 p-8 rounded-2xl">
                    <div className="flex items-center gap-4 mb-4">
                      <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
                      <span className="font-sans text-[10px] font-bold tracking-[0.2em] text-[#141414]/70 uppercase">Monitoring Hub</span>
                    </div>
                    <p className="font-display font-light text-2xl text-[#141414] leading-tight">
                      "Effective security depends on timely information & disciplined execution."
                    </p>
                  </div>
                </div>
              </div>
            </FadeUp>
            
            <div className="order-1 lg:order-2">
              <FadeUp>
                <span className="section-label block mb-6 text-[#835F21]">Operational Approach</span>
                <h2 className="font-display font-medium text-[#141414] leading-[1.1] mb-8 tracking-tight"
                  style={{ fontSize: "clamp(40px, 4vw, 50px)" }}
                >
                  Structured & Transparent<br />
                  <span className="text-[#835F21] italic font-light">Execution</span>
                </h2>
                <p className="font-sans text-lg text-[#141414]/60 leading-relaxed mb-16 font-light">
                 We follow a clear and practical process to ensure every deployment is efficient and aligned with client requirements:      </p>
                
                <div className="space-y-10 mb-16 relative before:absolute before:inset-y-0 before:left-6 before:w-[1px] before:bg-[#141414]/10">
                  {[
                    { title: "Site Assessment", desc: "We evaluate site risks, access points, and operational needs before deployment." },
                    { title: "Personnel Training & Allocation", desc: "Staff are trained and assigned based on site-specific requirements." },
                    { title: "Monitoring & Reporting", desc: "Daily logs, attendance tracking, and incident reporting ensure accountability." },
                  ].map((item, i) => (
                    <div key={i} className="flex gap-8 group relative z-10">
                      <div className="w-12 h-12 rounded-full bg-[#F9F8F5] border border-[#141414]/20 flex items-center justify-center flex-shrink-0 group-hover:border-[#835F21] group-hover:bg-[#835F21]/10 transition-colors shadow-xl">
                        <span className="font-sans text-[11px] font-bold text-[#835F21]">0{i+1}</span>
                      </div>
                      <div className="pt-2">
                        <h4 className="font-display font-medium text-xl md:text-2xl text-[#141414] mb-3 tracking-tight">{item.title}</h4>
                        <p className="font-sans text-[15px] text-[#141414]/50 leading-relaxed font-light">{item.desc}</p>
                      </div>
                    </div>
                  ))}
                </div>

                <Link to="/how-we-work" className="inline-flex items-center gap-3 font-sans text-xs font-bold tracking-[0.2em] text-[#141414] uppercase group hover:text-[#835F21] transition-colors">
                  View Full Framework
                  <div className="w-10 h-10 rounded-full border border-[#141414]/20 flex items-center justify-center group-hover:border-[#835F21] transition-colors">
                    <ChevronRight size={16} />
                  </div>
                </Link>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>

      {/* ── 05. Strategic Sectors ── */}
      <section className="bg-[#F0EBE6] py-16 md:py-24 overflow-hidden" data-testid="home-sectors">
        <div className="max-w-[1400px] mx-auto px-6">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-20 gap-10">
            <FadeUp className="max-w-3xl">
              <span className="font-sans text-[11px] font-bold tracking-[0.2em] text-[#835F21] uppercase block mb-6">Our Capabilities</span>
              <h2 className="font-display font-medium text-[#141414] leading-[1.05] tracking-tight" style={{ fontSize: "clamp(44px, 5vw, 72px)" }}>
                Complete Security & <br /> <span className="italic font-light">Support Services</span>
              </h2>
            </FadeUp>
            <FadeUp delay={100}>
              <Link to="/services" className="btn-secondary border-[#141414]/20 text-[#141414] bg-transparent hover:bg-white h-14">
                Explore All Services
              </Link>
            </FadeUp>
          </div>
          
          <div className="racesWrapper overflow-visible" ref={sectorsWrapperRef}>
            <div className="flex w-max gap-8 md:gap-12 pb-24 px-[10vw]" ref={sectorsRef}>
              {servicesList.map((service, i) => (
                <div key={i} className="w-[85vw] md:w-[50vw] lg:w-[32vw] shrink-0">
                  <div className="group relative aspect-[4/5] lg:aspect-[3/3] rounded-[2rem] overflow-hidden bg-[#141414] transition-all duration-700 hover:-translate-y-2 hover:shadow-2xl">
                    {/* Background Image */}
                    <img src={service.img} alt={service.name} className="absolute inset-0 w-full h-full object-cover opacity-70 transition-transform duration-1000 group-hover:scale-110" />
                    
                    {/* Overlay Gradient */}
                    <div className="absolute inset-0 bg-gradient-to-t from-black via-black/50 to-transparent transition-opacity group-hover:opacity-90" />
                    
                    {/* Content */}
                    <div className="absolute inset-0 p-10 flex flex-col justify-end translate-y-6 group-hover:translate-y-0 transition-transform duration-500">
                      <hr className="w-12 border-t-2 border-[#835F21] mb-6 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                      <h3 className="font-display font-medium text-2xl lg:text-3xl text-white mb-3 leading-tight tracking-tight">{service.name}</h3>
                      <p className="font-sans text-[15px] font-light text-white/70 mb-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700 leading-relaxed">
                        Protocol: {service.focus}
                      </p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── 06. Authority CTA Section ── */}
      <section className="bg-[#F9F8F5] pb-20 pt-10 md:pb-32 md:pt-16" data-testid="home-cta">
        <div className="max-w-7xl mx-auto px-6">
          <div className="bg-[#141414] rounded-[3rem] md:rounded-[4rem] p-16 md:p-32 text-center relative overflow-hidden shadow-2xl group">
            {/* Background Texture/Gradient */}
            <div className="absolute inset-0 bg-[#835F21] opacity-0 group-hover:opacity-5 transition-opacity duration-1000" />
            <div className="absolute top-0 right-0 w-[800px] h-[800px] bg-gradient-to-bl from-[#835F21]/20 to-transparent rounded-full blur-[100px] -translate-y-1/2 translate-x-1/3" />
            
            <div className="relative z-10 max-w-4xl mx-auto">
              <FadeUp>
                <Target size={48} className="text-[#835F21] mx-auto mb-10 stroke-[1px]" />
                <h2 className="font-display font-medium text-white leading-[1.05] mb-10 tracking-tight" style={{ fontSize: "clamp(48px, 6vw, 84px)" }}>
                  Start Your <br /> <span className="italic font-light text-[#835F21]">Security Partnership.</span>
                </h2>
                <p className="font-sans text-lg md:text-xl text-white/50 font-light max-w-2xl mx-auto mb-16">
                 Connect with Astraum Task Force to build a structured, reliable, and scalable security and manpower solution tailored to your organization.
                </p>
                <div className="flex flex-col sm:flex-row justify-center gap-6">
                  <Link to="/contact" className="btn-primary h-16 px-12 md:px-16 text-xs sm:text-sm bg-white text-[#141414] hover:bg-white/90 hover:text-black shadow-[0_0_40px_rgba(255,255,255,0.1)]">
                    Schedule Consultation
                  </Link>
                  <Link to="/about" className="btn-secondary h-16 px-12 md:px-16 text-xs sm:text-sm border-white/20 text-white hover:bg-white/5 hover:border-white/40">
                    Review Credentials
                  </Link>
                </div>
              </FadeUp>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
